import { z } from 'zod';

export const crmCustomerDirectoryPageSize = 10 as const;
export const crmCustomerDirectoryMaxPage = 100_000 as const;
export const crmCustomerDirectoryMaxFilterValues = 50 as const;

export function crmCustomerDirectorySearchArraySchema<T extends z.ZodType<string, string>>(
  itemSchema: T
) {
  const arraySchema = z.array(itemSchema).max(crmCustomerDirectoryMaxFilterValues);
  return z
    .union([
      arraySchema,
      z
        .string()
        .transform((value) => {
          try {
            const parsed = JSON.parse(value) as unknown;
            if (!Array.isArray(parsed)) return [value];
            const parsedValues = parsed.filter((item): item is string => typeof item === 'string');
            return parsedValues.length === parsed.length ? parsedValues : [value];
          } catch {
            return [value];
          }
        })
        .pipe(arraySchema),
    ])
    .optional();
}

export const crmCustomerDirectoryViewIds = [
  'crm.customerDirectory.v1',
  'crm.customerDirectory.freedivers.v1',
  'crm.customerDirectory.instructors.v1',
  'crm.customerDirectory.membership.v1',
  'crm.customerDirectory.shopify.v1',
  'crm.customerDirectory.discounts.v1',
  'crm.customerDirectory.documents.v1',
] as const;
export const crmCustomerDirectoryViewIdSchema = z.enum(crmCustomerDirectoryViewIds);
export const crmCustomerDirectoryViewId = crmCustomerDirectoryViewIds[0];

export const crmCustomerDirectoryFilterIds = [
  'businessLineKeys',
  'linkedSystems',
  'freshnessState',
  'roles',
  'preferredLanguages',
  'cities',
  'regions',
  'mainLocationIds',
  'certificationTypeIds',
  'membershipTypeIds',
  'currentMembershipStatuses',
  'shopifyId',
  'userOrigins',
] as const;
export const crmCustomerDirectoryFilterIdSchema = z.enum(crmCustomerDirectoryFilterIds);
export const crmCustomerDirectoryColumnIdSchema = z.enum([
  'user',
  'freediverId',
  'instructorId',
  'roles',
  'birthDate',
  'submittingInstructor',
  'preferredLanguage',
  'personalAddress',
  'registrationsCount',
  'lastCertification',
  'inductingInstructor',
  'currentMembershipNumber',
  'currentMembershipType',
  'currentMembershipStatus',
  'currentMembershipExpiration',
  'shopifyId',
  'shopifyTags',
  'userOrigin',
  'country',
  'city',
  'volumeDiscount',
  'highestDiscount',
  'lastDocumentType',
  'lastDocumentStatus',
  'lastDocumentValidUntil',
  'freshnessState',
  'updatedAt',
  'actions',
]);
export const crmCustomerDirectoryFreshnessStateSchema = z.enum(['fresh', 'stale', 'unknown']);
export const crmCustomerDirectorySortSchema = z.enum([
  'displayName.asc',
  'displayName.desc',
  'birthDate.asc',
  'birthDate.desc',
  'preferredLanguage.asc',
  'preferredLanguage.desc',
  'shopifyId.asc',
  'shopifyId.desc',
  'userOrigin.asc',
  'userOrigin.desc',
  'country.asc',
  'country.desc',
  'city.asc',
  'city.desc',
  'freediverId.asc',
  'freediverId.desc',
  'volumeDiscount.asc',
  'volumeDiscount.desc',
  'highestDiscount.asc',
  'highestDiscount.desc',
  'updatedAt.asc',
  'updatedAt.desc',
]);
export const crmCustomerDirectoryCountModeSchema = z.enum(['exact', 'unavailable']);
export const crmCustomerDirectoryCacheStatusSchema = z.enum(['hit', 'miss', 'not_applicable']);

const nullableTextSchema = z.string().nullable();
const optionSchema = z.object({ label: z.string(), value: z.string() });

export const crmCustomerDirectoryFiltersSchema = z.object({
  businessLineKeys: z.array(z.literal('movement')).optional(),
  linkedSystems: z.array(z.enum(['bubble', 'move'])).optional(),
  freshnessState: crmCustomerDirectoryFreshnessStateSchema.optional(),
  roles: z.array(z.string()).optional(),
  preferredLanguages: z.array(z.string()).optional(),
  cities: z.array(z.string()).optional(),
  regions: z.array(z.string()).optional(),
  mainLocationIds: z.array(z.uuid()).optional(),
  certificationTypeIds: z.array(z.uuid()).optional(),
  membershipTypeIds: z.array(z.uuid()).optional(),
  currentMembershipStatuses: z.array(z.string()).optional(),
  shopifyId: z.string().optional(),
  userOrigins: z.array(z.enum(['Shopify', 'Move'])).optional(),
});

export const crmCustomerDirectoryRowSchema = z.object({
  accountId: z.uuid().nullable(),
  allowedActions: z.tuple([z.literal('read')]).readonly(),
  customerId: z.uuid(),
  user: z.object({
    avatarUrl: z.url().nullable(),
    displayName: z.string(),
    email: z.string().nullable(),
  }),
  roles: z.array(z.string()),
  freshnessState: crmCustomerDirectoryFreshnessStateSchema,
  updatedAt: nullableTextSchema,
  freediverId: nullableTextSchema.optional(),
  instructorId: nullableTextSchema.optional(),
  birthDate: nullableTextSchema.optional(),
  submittingInstructor: z.object({ id: z.uuid(), name: z.string() }).nullable().optional(),
  preferredLanguage: nullableTextSchema.optional(),
  personalAddress: nullableTextSchema.optional(),
  registrationsCount: z.number().int().nonnegative().nullable().optional(),
  lastCertification: z
    .object({ certifiedAt: nullableTextSchema, type: nullableTextSchema })
    .nullable()
    .optional(),
  inductingInstructor: z.object({ id: z.uuid(), name: z.string() }).nullable().optional(),
  currentMembership: z
    .object({
      expiresAt: nullableTextSchema,
      number: nullableTextSchema,
      status: nullableTextSchema,
      type: z.object({ id: z.uuid().nullable(), name: nullableTextSchema }),
    })
    .nullable()
    .optional(),
  shopify: z
    .object({ id: nullableTextSchema, tags: z.array(z.string()) })
    .nullable()
    .optional(),
  userOrigin: z.enum(['Shopify', 'Move']).nullable().optional(),
  country: nullableTextSchema.default(null),
  city: nullableTextSchema.default(null),
  discounts: z.object({ highest: z.number().nullable(), volume: z.number().nullable() }).optional(),
  lastDocument: z
    .object({
      status: nullableTextSchema,
      type: nullableTextSchema,
      validUntil: nullableTextSchema,
    })
    .nullable()
    .optional(),
});

export const crmCustomerDirectorySegmentDistributionSchema = z.object({
  buckets: z.array(
    z.object({ count: z.number().int().nonnegative(), label: z.string(), value: z.string() })
  ),
  dimensionId: z.literal('freshnessState'),
  label: z.string(),
  unknownCount: z.number().int().nonnegative(),
});

export const crmCustomerDirectoryFilterDefinitionSchema = z.object({
  id: crmCustomerDirectoryFilterIdSchema,
  input: z.enum(['multi-select', 'search-multi-select', 'search-select', 'single-select']),
  minimumSearchLength: z.number().int().positive().optional(),
  optionLimit: z.number().int().positive().optional(),
  options: z.array(optionSchema).optional(),
});
const crmCustomerDirectoryMandatoryFilterSchema = z.union([
  z.object({
    field: z.literal('roles'),
    operator: z.literal('contains'),
    value: z.enum(['Freediver', 'Instructor']),
  }),
  z.object({
    field: z.enum(['currentMembership', 'shopifyId', 'lastDocument']),
    operator: z.literal('notEmpty'),
  }),
]);

export const crmCustomerDirectoryResponseSchema = z
  .object({
    data: z.object({
      capabilities: z
        .object({
          actions: z.array(z.literal('openProfile')),
          filters: z.array(crmCustomerDirectoryFilterDefinitionSchema),
          sorts: z.array(crmCustomerDirectorySortSchema),
          views: z.array(
            z.object({
              columns: z.array(crmCustomerDirectoryColumnIdSchema),
              id: crmCustomerDirectoryViewIdSchema,
              label: z.string(),
              mandatoryFilters: z.array(crmCustomerDirectoryMandatoryFilterSchema),
            })
          ),
        })
        .nullable(),
      filterOptions: z
        .object({
          filterId: crmCustomerDirectoryFilterIdSchema,
          hasMore: z.boolean(),
          options: z.array(optionSchema).max(20),
        })
        .nullable()
        .default(null),
      query: z.object({
        filters: crmCustomerDirectoryFiltersSchema,
        q: z.string().nullable(),
        sort: crmCustomerDirectorySortSchema,
        viewId: crmCustomerDirectoryViewIdSchema,
      }),
      rows: z.array(crmCustomerDirectoryRowSchema).max(crmCustomerDirectoryPageSize),
      segment: z.object({
        distributions: z.array(crmCustomerDirectorySegmentDistributionSchema),
        total: z.object({
          mode: crmCustomerDirectoryCountModeSchema,
          reason: z.string().optional(),
          value: z.number().int().nonnegative(),
        }),
      }),
    }),
    meta: z.object({
      countMode: crmCustomerDirectoryCountModeSchema,
      diagnostics: z
        .object({
          cache: z.object({
            exactCount: crmCustomerDirectoryCacheStatusSchema,
            exactCountAgeMs: z.number().nonnegative().optional(),
          }),
          exactCountSqlMs: z.number().nonnegative().optional(),
          rowSqlMs: z.number().nonnegative().optional(),
          segmentSqlMs: z.number().nonnegative().optional(),
          totalServiceMs: z.number().nonnegative(),
        })
        .optional(),
      hasNextPage: z.boolean(),
      nextCursor: z.string().min(1).nullable(),
      page: z.number().int().positive(),
      pageSize: z.literal(crmCustomerDirectoryPageSize),
      totalItems: z.number().int().nonnegative(),
      totalPages: z.number().int().nonnegative(),
    }),
  })
  .superRefine((response, context) => {
    if (response.meta.hasNextPage && response.data.rows.length !== crmCustomerDirectoryPageSize) {
      context.addIssue({
        code: 'custom',
        message: 'A CRM page with another page must contain exactly ten rows.',
        path: ['data', 'rows'],
      });
    }
  });

export type CrmCustomerDirectoryViewId = z.infer<typeof crmCustomerDirectoryViewIdSchema>;
export type CrmCustomerDirectoryFilterId = z.infer<typeof crmCustomerDirectoryFilterIdSchema>;
export type CrmCustomerDirectoryFilters = z.infer<typeof crmCustomerDirectoryFiltersSchema>;
export type CrmCustomerDirectoryFreshnessState = z.infer<
  typeof crmCustomerDirectoryFreshnessStateSchema
>;
export type CrmCustomerDirectorySort = z.infer<typeof crmCustomerDirectorySortSchema>;
export type CrmCustomerDirectoryApiRow = z.infer<typeof crmCustomerDirectoryRowSchema>;
export type CrmCustomerDirectoryResponse = z.infer<typeof crmCustomerDirectoryResponseSchema>;
export type CrmCustomerDirectorySegmentDistribution = z.infer<
  typeof crmCustomerDirectorySegmentDistributionSchema
>;

export interface CrmCustomerDirectoryQueryParams extends CrmCustomerDirectoryFilters {
  cursor?: string;
  filterOptionId?: CrmCustomerDirectoryFilterId;
  filterOptionQuery?: string;
  filterOptionValues?: string[];
  includeCapabilities?: boolean;
  includeRows?: boolean;
  includeSegment?: boolean;
  page?: number;
  q?: string;
  sort: CrmCustomerDirectorySort;
  viewId: CrmCustomerDirectoryViewId;
}
